'use client';
import React from 'react';
import { Distributor, getEBRate, DISTRIBUTORS } from '@/data/mockData';
import { DollarSign } from 'lucide-react';

interface Props {
  distributor: Distributor;
}

export default function CommissionRightPanel({ distributor }: Props) {
  const isExec = ['Executive', 'Provisional Executive', 'Emerald', 'Diamond', 'Blue Diamond', 'Presidential Director'].includes(distributor.rank);
  const ebRate = getEBRate(distributor.gsv);
  const hasEEB = distributor.gsv >= 3000;
  const hasDblG1 = distributor.gsv >= 3000;

  // Commission calculations
  const retailProfit = distributor.psv * 0.25; // ~25% retail markup
  const l1Bonus = distributor.children.reduce((sum, id) => {
    const child = DISTRIBUTORS[id];
    return sum + (child ? child.psv * 0.05 : 0);
  }, 0);
  const execBonus = isExec ? distributor.gsv * ebRate : 0;
  const eebBonus = isExec && hasEEB ? distributor.gsv * 0.05 : 0;
  const dblG1Bonus = isExec && hasDblG1 && distributor.g1BreakawayCount > 0 ? distributor.gsv * 0.10 : 0;
  const breakawayBonus = isExec && distributor.g1BreakawayCount >= 1 ? distributor.gsv * 0.025 : 0;

  // Wealth Maximizer: higher of Volume or Depth
  const volumePath = execBonus + eebBonus + dblG1Bonus;
  const depthPath = execBonus + breakawayBonus;
  const wealthMaxBonus = Math.max(volumePath, depthPath);
  const wealthPath = volumePath >= depthPath ? 'Volume' : 'Depth';

  const totalComm = retailProfit + l1Bonus + (isExec ? wealthMaxBonus : 0);

  const components = [
    { label: 'Retail Profit (GRP)', value: retailProfit, desc: `${distributor.psv} PSV × 25%`, always: true },
    { label: 'L1 Bonus', value: l1Bonus, desc: `${distributor.children.length} sponsored × 5% PSV`, always: true },
    { label: `Exec Bonus (EB ${(ebRate*100).toFixed(0)}%)`, value: execBonus, desc: `${(distributor.gsv/1000).toFixed(1)}K GSV × ${(ebRate*100).toFixed(0)}%`, always: false },
    { label: 'Extra Exec Bonus (EEB)', value: eebBonus, desc: hasEEB ? 'GSV ≥ 3K → +5% GCSV' : 'Requires 3,000+ GSV', always: false },
    { label: 'Double G1 Bonus (DBLG1)', value: dblG1Bonus, desc: hasDblG1 ? `${distributor.g1BreakawayCount} G1 execs × 10%` : 'Requires 3,000+ GSV', always: false },
    { label: 'Breakaway Exec Bonus', value: breakawayBonus, desc: `${distributor.g1BreakawayCount} G1 execs`, always: false },
  ];

  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Commission Breakdown</span>
        <span className="text-[10px] text-zinc-600">Apr 2026</span>
      </div>

      {/* Total Commission */}
      <div className="bg-gradient-to-br from-violet-900/30 to-violet-800/10 border border-violet-500/20 rounded-xl p-3">
        <p className="text-[10px] uppercase tracking-widest text-violet-400 font-semibold mb-1">Total Estimated Commission</p>
        <p className="text-3xl font-bold text-violet-200 font-mono tabular-nums">${totalComm.toFixed(2)}</p>
        <p className="text-[10px] text-violet-400/60 mt-0.5">Wealth Maximizer: {wealthPath} Path selected</p>
      </div>

      {/* Wealth Maximizer */}
      {isExec && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 space-y-2">
          <div className="flex items-center gap-1.5">
            <DollarSign size={12} className="text-amber-400" />
            <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Wealth Maximizer</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Volume Maximizer', value: volumePath, active: wealthPath === 'Volume', desc: 'EB + EEB + DBLG1' },
              { label: 'Depth Maximizer', value: depthPath, active: wealthPath === 'Depth', desc: 'EB + Breakaway EB' },
            ].map(p => (
              <div key={`wm-${p.label}`} className={`rounded-lg p-2 border ${p.active ? 'bg-amber-500/10 border-amber-500/30' : 'bg-zinc-800/40 border-zinc-700'}`}>
                <p className="text-[9px] text-zinc-500 mb-1">{p.desc}</p>
                <p className={`text-sm font-bold font-mono ${p.active ? 'text-amber-400' : 'text-zinc-400'}`}>${p.value.toFixed(2)}</p>
                <p className={`text-[9px] font-semibold mt-0.5 ${p.active ? 'text-amber-400' : 'text-zinc-600'}`}>
                  {p.active ? '✓ Selected' : p.label.split(' ')[0]}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Commission Components */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-3 py-2 border-b border-zinc-800">
          <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">Component Breakdown</span>
        </div>
        <div className="divide-y divide-zinc-800/60">
          {components.map(comp => {
            const eligible = comp.always || isExec;
            return (
              <div key={`comp-${comp.label}`} className={`flex items-center justify-between px-3 py-2 ${!eligible ? 'opacity-40' : ''}`}>
                <div className="min-w-0 flex-1 pr-2">
                  <p className="text-[10px] font-semibold text-zinc-300 truncate">{comp.label}</p>
                  <p className="text-[9px] text-zinc-600 truncate">{comp.desc}</p>
                </div>
                <span className={`text-xs font-bold font-mono flex-shrink-0 ${comp.value > 0 ? 'text-emerald-400' : 'text-zinc-600'}`}>
                  ${comp.value.toFixed(2)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Maintenance Status */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 space-y-2">
        <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">Maintenance Status</span>
        <div className="space-y-1.5">
          {[
            { label: 'PSV ≥ 100', ok: distributor.psv >= 100, value: `${distributor.psv} PSV` },
            { label: 'GSV ≥ 2,000 (Exec)', ok: !isExec || distributor.gsv >= 2000, value: `${(distributor.gsv/1000).toFixed(1)}K GSV` },
            { label: 'ADR Active', ok: distributor.adrActive, value: distributor.adrActive ? 'On' : 'Off' },
            { label: 'Grace Month', ok: !distributor.graceMonthUsed, value: distributor.graceMonthUsed ? 'Used' : 'Available' },
          ].map(item => (
            <div key={`maint-${item.label}`} className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className={`text-[10px] font-semibold ${item.ok ? 'text-emerald-400' : 'text-red-400'}`}>{item.ok ? '✓' : '✗'}</span>
                <span className="text-[10px] text-zinc-400">{item.label}</span>
              </div>
              <span className={`text-[10px] font-mono font-semibold ${item.ok ? 'text-zinc-400' : 'text-red-400'}`}>{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}